import React, { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';

type Props = {
  to?: string;
  text?: string;
  className?:string;
  containerStyle?: CSSProperties;
  colorScheme?: 'primary' | 'secondary';
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>,
  ) => void;
};

export const Button: React.FC<Props> = ({
  to,
  onClick,
  containerStyle,
  className,
  text = 'Button',
  colorScheme = 'primary',
}) => {
  const navigate = useNavigate();

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    if (to === 'back') {
      event.preventDefault();
      navigate(-1);
    } else if (onClick) {
      onClick(event);
    }
  };

  const baseStyle: CSSProperties = {
    height: 50,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.7,
    cursor: 'pointer',
    margin: '0 auto',
    textAlign: 'center',
    textTransform: 'capitalize',
    width: '100%',
    userSelect: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: 'none',
    outline: 'none',
    position: 'relative',
    overflow: 'hidden',
  };

  const primaryStyle: CSSProperties = {
    ...baseStyle,
    backgroundColor: 'var(--main-turquoise)',
    color: 'var(--white-color)',
    boxShadow: '0 4px 12px rgba(0, 176, 185, 0.3)',
  };

  const secondaryStyle: CSSProperties = {
    ...baseStyle,
    backgroundColor: 'transparent',
    color: 'var(--main-turquoise)',
    border: '2px solid var(--main-turquoise)',
    boxShadow: '0 2px 8px rgba(0, 176, 185, 0.2)',
  };

  const style: CSSProperties = {
    ...(colorScheme === 'primary' ? primaryStyle : secondaryStyle),
    ...containerStyle,
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    const element = e.currentTarget;
    if (colorScheme === 'primary') {
      element.style.backgroundColor = '#00a0a8';
      element.style.boxShadow = '0 6px 16px rgba(0, 176, 185, 0.4)';
      element.style.transform = 'translateY(-2px)';
    } else {
      element.style.backgroundColor = 'rgba(0, 176, 185, 0.05)';
      element.style.boxShadow = '0 4px 12px rgba(0, 176, 185, 0.3)';
      element.style.transform = 'translateY(-2px)';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    const element = e.currentTarget;
    if (colorScheme === 'primary') {
      element.style.backgroundColor = 'var(--main-turquoise)';
      element.style.boxShadow = '0 4px 12px rgba(0, 176, 185, 0.3)';
      element.style.transform = 'translateY(0)';
    } else {
      element.style.backgroundColor = 'transparent';
      element.style.boxShadow = '0 2px 8px rgba(0, 176, 185, 0.2)';
      element.style.transform = 'translateY(0)';
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.transform = 'translateY(0)';
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    if (colorScheme === 'primary') {
      e.currentTarget.style.transform = 'translateY(-2px)';
    } else {
      e.currentTarget.style.transform = 'translateY(-2px)';
    }
  };

  if (to) {
    return (
      <Link
        to={to}
        style={{...style}}
        className={className}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {text}
      </Link>
    );
  }

  if (!to) {
    return (
      <button
        style={{...style}}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {text}
      </button>
    );
  }

  return null;
};
